import { forwardRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { employeeApi } from '../api/hr';

/**
 * A real staff picker, reused everywhere a form currently asks for a
 * raw numeric employee ID - Travel, Logistics, Assets, Fleet, HR
 * (Probation, Separation, Restricted Cases, Staff Movement), and any
 * future module. Fetches the universally-accessible employee
 * directory (employees.view_directory - every role, per migration
 * 013) via the existing employeeApi.directory() rather than the
 * restricted full HR listing, since this component has no need for
 * sensitive fields.
 *
 * Built as a plain forwardRef <select> so it drops straight into the
 * existing react-hook-form register() pattern used throughout the
 * codebase - no Controller wrapper needed:
 *   <EmployeePicker {...register('travellerEmployeeId', { required: true, valueAsNumber: true })} />
 *
 * excludeId is optional and additive - every existing caller is
 * unaffected. Use it where a self-reference is nonsensical, e.g. an
 * employee's own supervisor picker should not offer that same
 * employee as an option.
 */
export const EmployeePicker = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement> & { excludeId?: number }>(
  function EmployeePicker({ excludeId, ...props }, ref) {
    const { data, isLoading } = useQuery({
      queryKey: ['employee-directory'],
      queryFn: () => employeeApi.directory(),
      staleTime: 5 * 60 * 1000, // the directory changes rarely within a session - avoid refetching on every form open
    });

    const employees = (data?.data ?? []).filter((e) => e.id !== excludeId);

    return (
      <select ref={ref} {...props} disabled={isLoading || props.disabled}>
        <option value="">{isLoading ? 'Loading staff…' : 'Select staff member…'}</option>
        {employees.map((e) => (
          <option key={e.id} value={e.id}>
            {e.full_name}{e.position_name ? ` — ${e.position_name}` : ''}{e.department_name ? `, ${e.department_name}` : ''}
          </option>
        ))}
      </select>
    );
  }
);
